import React from "react";

import { App } from "antd";
import {
  Activity,
  FlaskConical,
  LayoutDashboard,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { INNOVATION_ROUTES } from "../../../routes";
import { useUpdateExperimentStatus } from "../../experiments/api/experimentsApi";
import { getExperimentStepPath } from "../../experiments/routes";
import { DashboardExperimentBrowser } from "../components/DashboardExperimentBrowser";
import { DashboardLiveExperimentRow } from "../components/DashboardLiveExperimentRow";
import { DashboardMetricCard } from "../components/DashboardMetricCard";
import { DashboardPipelineTracker } from "../components/DashboardPipelineTracker";
import { useDashboardState } from "../hooks/useDashboardState";
import { formatCount } from "../utils/dashboardUtils";

import "../components/dashboard.css";

import type {
  DashboardMetricCardConfig,
  ExperimentData,
} from "../types/dashboard.types";

export const DashboardHomePage: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { mutateAsync: updateExperimentStatus } = useUpdateExperimentStatus();
  const [deletingExperimentId, setDeletingExperimentId] = React.useState<
    string | null
  >(null);
  const {
    trackerExperiments,
    statusCounts,
    pipelineStages,
    liveMetrics,
    pagedExperiments,
    filteredExperiments,
    mainTab,
    setMainTab,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    cardsLoaded,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    isLoading,
  } = useDashboardState();
  const totalExperiments = Object.values(statusCounts).reduce(
    (sum, count) => sum + count,
    0,
  );

  const metricCards: DashboardMetricCardConfig[] = [
    {
      key: "active-count",
      title: "Live",
      value: liveMetrics.activeCount,
      label: "Experiments",
    },
    {
      key: "participants",
      title: "Live Participants",
      value: formatCount(liveMetrics.totalParticipants),
      label: "Total",
    },
    {
      key: "completion",
      title: "Avg Completion",
      value: `${liveMetrics.avgCompletionRate}%`,
      label: "Rate",
    },
    {
      key: "events",
      title: "Events (24h)",
      value: formatCount(liveMetrics.totalEvents24h),
      label: "Total",
    },
  ];

  const metricIcons = {
    "active-count": <Zap size={14} />,
    participants: <Users size={14} />,
    completion: <TrendingUp size={14} />,
    events: <Activity size={14} />,
  } as const;
  const getExperimentReportPath = (
    experimentId: string,
    experimentName: string,
  ) =>
    `${INNOVATION_ROUTES.EXPERIMENT_REPORT}?id=${encodeURIComponent(
      experimentId,
    )}&name=${encodeURIComponent(experimentName)}`;

  const handleDeleteExperiment = React.useCallback(
    (experiment: ExperimentData) => {
      modal.confirm({
        title: "Delete experiment?",
        content: `Are you sure you want to delete "${experiment.name}"?`,
        okText: "Delete",
        cancelText: "Cancel",
        okButtonProps: {
          danger: true,
        },
        centered: true,
        onOk: async () => {
          setDeletingExperimentId(experiment.id);

          try {
            await updateExperimentStatus({
              experimentId: experiment.id,
              status: 6,
            });
            message.success("Experiment deleted.");
          } catch (error) {
            message.error(
              error instanceof Error
                ? error.message
                : "Failed to delete experiment.",
            );
            throw error;
          } finally {
            setDeletingExperimentId((currentExperimentId) =>
              currentExperimentId === experiment.id
                ? null
                : currentExperimentId,
            );
          }
        },
      });
    },
    [message, modal, updateExperimentStatus],
  );

  return (
    <div className="innovation-dashboard">
      <div className="innovation-dashboard__page">
        <div
          className="innovation-dashboard__tabs"
          role="tablist"
          aria-label="Innovation tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === "dashboard"}
            className={`innovation-dashboard__tab${mainTab === "dashboard" ? " is-active" : ""}`}
            onClick={() => setMainTab("dashboard")}
          >
            <LayoutDashboard size={14} />
            <span>Dashboard</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === "experiments"}
            className={`innovation-dashboard__tab${mainTab === "experiments" ? " is-active" : ""}`}
            onClick={() => setMainTab("experiments")}
          >
            <FlaskConical size={14} />
            <span>Experiments</span>
            <span className="innovation-dashboard__tab-badge">
              {totalExperiments}
            </span>
          </button>
        </div>

        {mainTab === "dashboard" ? (
          <section className="innovation-dashboard__panel">
            <div className="innovation-dashboard__title-block">
              <div className="innovation-dashboard__title-row">
                <span
                  className="innovation-dashboard__title-accent"
                  aria-hidden="true"
                />
                <h1 className="innovation-dashboard__title">
                  Experiment Tracker
                </h1>
              </div>
              <p className="innovation-dashboard__title-subtitle">
                Monitor the experiment pipeline and review live study activity
                as work moves through Falcon.
              </p>
            </div>

            {isLoading ? (
              <div className="innovation-dashboard__loading-state innovation-dashboard__loading-state--panel">
                <div
                  className="innovation-dashboard__loading-spinner"
                  aria-hidden="true"
                />
                <div className="innovation-dashboard__loading-title">
                  Loading experiment tracker
                </div>
                <p className="innovation-dashboard__loading-copy">
                  Fetching live experiment status, participant counts, and
                  recent activity.
                </p>
              </div>
            ) : (
              <>
                <DashboardPipelineTracker stages={pipelineStages} />

                <div className="innovation-dashboard__metric-grid">
                  {metricCards.map((metricCard) => (
                    <DashboardMetricCard
                      key={metricCard.key}
                      title={metricCard.title}
                      value={metricCard.value}
                      label={metricCard.label}
                      icon={
                        metricIcons[metricCard.key as keyof typeof metricIcons]
                      }
                    />
                  ))}
                </div>

                <div className="innovation-dashboard__section">
                  <div className="innovation-dashboard__section-title">
                    Active Experiments
                  </div>
                  {trackerExperiments.length > 0 ? (
                    <div className="innovation-dashboard__live-list">
                      {trackerExperiments.map((experiment) => (
                        <DashboardLiveExperimentRow
                          key={experiment.id}
                          experiment={experiment}
                          onOpenExperiment={() =>
                            navigate(
                              getExperimentStepPath("context", experiment.id),
                            )
                          }
                          onViewInsights={() =>
                            navigate(
                              getExperimentReportPath(
                                experiment.id,
                                experiment.name,
                              ),
                            )
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="innovation-dashboard__live-empty">
                      <div className="innovation-dashboard__live-empty-icon">
                        <FlaskConical size={18} />
                      </div>
                      <div className="innovation-dashboard__live-empty-title">
                        No tracked experiments yet
                      </div>
                      <p className="innovation-dashboard__live-empty-copy">
                        Live, paused, and completed experiments will appear here
                        with participant, completion, and event tracking.
                      </p>
                      <button
                        type="button"
                        className="innovation-dashboard__empty-link"
                        onClick={() => setMainTab("experiments")}
                      >
                        View Experiments
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        ) : (
          <section className="innovation-dashboard__panel innovation-dashboard__panel--experiments">
            <DashboardExperimentBrowser
              isLoading={isLoading}
              searchQuery={searchQuery}
              onSearchChange={(value) => {
                setSearchQuery(value);
                setCurrentPage(1);
              }}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sortBy={sortBy}
              onSortChange={(nextSortBy) => {
                setSortBy(nextSortBy);
                setCurrentPage(1);
              }}
              statusFilter={statusFilter}
              onStatusChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              statusCounts={statusCounts}
              experiments={pagedExperiments}
              currentPage={currentPage}
              totalItems={filteredExperiments.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              cardsLoaded={cardsLoaded}
              onNewExperiment={() => navigate(getExperimentStepPath("context"))}
              onExperimentClick={(experiment) =>
                navigate(getExperimentStepPath("context", experiment.id))
              }
              onDeleteExperiment={handleDeleteExperiment}
              deletingExperimentId={deletingExperimentId}
            />
          </section>
        )}
      </div>
    </div>
  );
};

export default DashboardHomePage;
